/**
 * Photo onboarding (DESIGN.md §3.1, deck page 4) — two states, no narration.
 *
 * The choice: a heading, one line about why the app wants a face, an example
 * card, and two equally weighted sources. Then crop + confirm: the photo in a
 * tall frame with a card-shaped window over it that drags to reposition and
 * pinches to resize.
 *
 * Every guarantee in SPEC §6 survives the copy coming out. The photo never
 * leaves the device until "Use photo" — the frame is showing a bitmap held in
 * this component, not something already uploaded — and the crop the player
 * lands on is the crop that gets resized, compressed and sent. The camera roll
 * is offered beside the camera at every point rather than behind a failure.
 *
 * Consent is this screen too. "Take a selfie" *is* the explicit grant SPEC §6
 * asks for — nothing opens the camera until it is pressed — so the player is
 * asked once, by two equal buttons, instead of twice in weaker words.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PHOTO_MAX_HEIGHT, PHOTO_MAX_WIDTH } from '@idgame/protocol';
import type { PlayerId } from '@idgame/protocol';
import {
  CameraError,
  grabFrame,
  openCamera,
  preflightCamera,
  stopStream,
} from '../../lib/camera.js';
import {
  PhotoTooLargeError,
  loadImage,
  processPhoto,
  windowToSourceRect,
  type CropRect,
} from '../../lib/image.js';
import { sound } from '../../lib/audio.js';
import { useDraft, useDraftActions, useSubmitProfile } from '../../store/index.js';
import { LicenseCard } from '../../components/index.js';
import { Button, Notice, Screen } from '../../app/ui.js';

/** SPEC §6 — exactly one retake on the live-selfie path. */
const MAX_RETAKES = 1;

const CARD_ASPECT = PHOTO_MAX_WIDTH / PHOTO_MAX_HEIGHT;

/** The tall frame the photo covers: everything between the status row and the actions. */
const FRAME = 'rounded-card relative min-h-0 w-full flex-1 overflow-hidden bg-ink';

/**
 * The crop window's share of the frame, off the deck. The width cap is what
 * usually decides it, since the frame is taller than the card is shaped.
 */
const CROP_HEIGHT_RATIO = 0.58;
const CROP_MAX_WIDTH_RATIO = 0.8;

/** Arrow-key nudge for the crop, in CSS pixels. */
const NUDGE = 8;

/**
 * How far the crop can be pinched, as a multiple of the default size.
 *
 * The floor is what keeps a two-finger fumble from collapsing the window to a
 * dot that then has to be found again; the ceiling is computed per frame rather
 * than fixed, because "as large as the photo allows" is the only ceiling that
 * means anything — a crop bigger than the frame would be cropping nothing.
 */
const MIN_CROP_SCALE = 0.45;

/** Keyboard resize step, doing for size what `NUDGE` does for position. */
const SCALE_STEP = 0.08;

type Mode = 'choice' | 'starting' | 'live' | 'preview';

/**
 * What the player is cropping. Held here rather than in the store: it is a
 * full-resolution bitmap that has not been compressed yet, so it must not
 * outlive the screen or reach anything that talks to the server.
 */
interface Source {
  /**
   * Passed to `processPhoto` — the untouched capture, at full size. Null for a
   * photo that re-entered the flow from the draft, where the `<img>` in the
   * frame is the only copy and is the same bitmap anyway.
   */
  drawable: HTMLCanvasElement | HTMLImageElement | null;
  /** What the frame displays. */
  url: string;
  from: 'camera' | 'upload';
  width: number;
  height: number;
}

interface Box {
  width: number;
  height: number;
}

/** An illustration, not a person: this card is an example of the shape. */
const EXAMPLE_FACE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="320">
      <rect width="320" height="320" fill="#fff1d3"/>
      <circle cx="160" cy="126" r="56" fill="#c0ab8b"/>
      <path d="M44 320c0-66 52-110 116-110s116 44 116 110z" fill="#c0ab8b"/>
    </svg>`,
  );

export function CaptureScreen() {
  const navigate = useNavigate();
  const { roomCode = '' } = useParams<{ roomCode: string }>();

  const draft = useDraft();
  const { grantCameraConsent, setDraftPhoto, clearDraftPhoto } = useDraftActions();
  const submitProfile = useSubmitProfile();

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLImageElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  /**
   * A player who came back from the name screen to change their photo already
   * has one; it re-enters the flow as an ordinary source, so the crop window
   * works on it too. Its size is the card size by construction — `processPhoto`
   * produced it — and the `<img>` corrects that on load either way.
   */
  const [source, setSource] = useState<Source | null>(() =>
    draft.photo
      ? {
          drawable: null,
          url: draft.photo,
          from: draft.photoSource ?? 'upload',
          width: PHOTO_MAX_WIDTH,
          height: PHOTO_MAX_HEIGHT,
        }
      : null,
  );
  const [mode, setMode] = useState<Mode>(source ? 'preview' : 'choice');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Measured, because the crop maths is in CSS pixels over this box. */
  const [frame, setFrame] = useState<Box>({ width: 0, height: 0 });
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  /** The crop window's size as a multiple of its default. 1 until pinched. */
  const [scale, setScale] = useState(1);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  /**
   * Every pointer currently down on the window, so the second finger arriving
   * is what turns a drag into a pinch — and so lifting one finger of a pinch
   * hands the gesture back to the finger still on the glass rather than ending
   * it.
   */
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{
    startDistance: number;
    startScale: number;
    /** The crop's centre at pinch start; the window grows around it. */
    centreX: number;
    centreY: number;
  } | null>(null);
  /**
   * The committed crop, readable from a handler that has already queued a
   * setState this event — a pinch and the drag that resumes after it both need
   * the position as it is *now*, not as it was when the handler was created.
   */
  const cropRef = useRef(crop);
  cropRef.current = crop;

  /**
   * False once the component is gone. `getUserMedia` resolves long after the
   * user may have navigated away — without this, a stream that arrives late is
   * stored on a dead component and never stopped, and the phone's camera
   * indicator stays lit. On an app that just asked to photograph someone's
   * face, that is the single worst bug available.
   *
   * React Strict Mode's mount → unmount → mount cycle makes it reproducible in
   * development, and the generation counter below means the first mount's
   * pending stream is discarded rather than overwriting the second's.
   */
  const aliveRef = useRef(true);
  const generationRef = useRef(0);

  const cameraBlocked = preflightCamera() !== null;
  /** The retake allowance is spent, so the camera is no longer on offer. */
  const retakesSpent = draft.retakesUsed >= MAX_RETAKES;
  const cameraOffered = !cameraBlocked && !retakesSpent;

  const stop = useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    const video = videoRef.current;
    if (video) video.srcObject = null;
  }, []);

  const releaseSource = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    const generation = (generationRef.current += 1);
    // Pressing the button is the grant, and it is recorded before anything
    // touches `getUserMedia` (SPEC §6).
    grantCameraConsent();
    setProblem(null);
    setMode('starting');

    // Never run two cameras at once: a retake must release the old stream
    // before asking for a new one.
    stop();

    let stream: MediaStream;
    try {
      stream = await openCamera();
    } catch (error) {
      if (!aliveRef.current || generation !== generationRef.current) return;
      setProblem(error instanceof CameraError ? error.message : 'The camera did not start.');
      setMode('choice');
      return;
    }

    // Resolved too late, or a newer start() has superseded this one.
    if (!aliveRef.current || generation !== generationRef.current) {
      stopStream(stream);
      return;
    }

    streamRef.current = stream;
    const video = videoRef.current;
    if (video) {
      video.srcObject = stream;
      await video.play().catch(() => undefined);
    }

    // `play()` is awaited, so check once more before committing to 'live'.
    if (!aliveRef.current || generation !== generationRef.current) {
      stopStream(stream);
      streamRef.current = null;
      return;
    }
    setMode('live');
  }, [stop, grantCameraConsent]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      // Invalidate anything in flight, then release the hardware.
      generationRef.current += 1;
      stop();
      releaseSource();
    };
  }, [stop, releaseSource]);

  /* ------------------------------------------------------------------ */
  /* The crop window                                                     */
  /* ------------------------------------------------------------------ */

  useLayoutEffect(() => {
    const measure = () => {
      const box = frameRef.current?.getBoundingClientRect();
      setFrame({ width: box?.width ?? 0, height: box?.height ?? 0 });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [mode]);

  /** The window at scale 1: what the frame alone decides. */
  const baseCrop = cropWindowSize(frame);

  /**
   * The crop may grow until one of its edges reaches the frame. Clamping on
   * render as well as on set is what makes a rotation safe: the frame can
   * change under a scale that was legal when it was chosen.
   */
  const maxScale =
    baseCrop.width > 0 && baseCrop.height > 0
      ? Math.max(
          MIN_CROP_SCALE,
          Math.min(frame.width / baseCrop.width, frame.height / baseCrop.height),
        )
      : 1;
  const clampScale = (next: number) => Math.min(Math.max(next, MIN_CROP_SCALE), maxScale);

  const sizeAt = (at: number): Box => ({
    width: Math.round(baseCrop.width * at),
    height: Math.round(baseCrop.height * at),
  });
  const cropSize = sizeAt(clampScale(scale));

  // A new photo starts centred at the default size, which is where a face
  // usually is. Deliberately *not* keyed on the crop's current size: that would
  // recentre the window on every pinch, which is the one thing a pinch must not
  // do — it grows around the point the fingers are holding.
  useEffect(() => {
    const size = cropWindowSize(frame);
    setScale(1);
    setCrop({
      x: Math.max(0, (frame.width - size.width) / 2),
      y: Math.max(0, (frame.height - size.height) / 2),
    });
  }, [source, frame.width, frame.height]);

  const clampWithin = (x: number, y: number, size: Box) => ({
    x: Math.min(Math.max(x, 0), Math.max(0, frame.width - size.width)),
    y: Math.min(Math.max(y, 0), Math.max(0, frame.height - size.height)),
  });
  const clampCrop = (x: number, y: number) => clampWithin(x, y, cropSize);

  /** Resize about a fixed centre, then pull the result back inside the frame. */
  const resizeTo = (next: number, centreX: number, centreY: number) => {
    const scaled = clampScale(next);
    const size = sizeAt(scaled);
    setScale(scaled);
    setCrop(clampWithin(centreX - size.width / 2, centreY - size.height / 2, size));
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    capturePointer(event.currentTarget, event.pointerId, true);
    const pointers = pointersRef.current;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size >= 2) {
      // A second finger converts the gesture: the drag stops here, and the
      // window starts growing and shrinking around where it currently sits.
      const [a, b] = [...pointers.values()];
      if (!a || !b) return;
      dragRef.current = null;
      pinchRef.current = {
        startDistance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        startScale: clampScale(scale),
        centreX: cropRef.current.x + cropSize.width / 2,
        centreY: cropRef.current.y + cropSize.height / 2,
      };
      return;
    }

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: cropRef.current.x,
      originY: cropRef.current.y,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    if (pointers.has(event.pointerId)) {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    const pinch = pinchRef.current;
    if (pinch && pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return;
      event.preventDefault();
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      resizeTo((pinch.startScale * distance) / pinch.startDistance, pinch.centreX, pinch.centreY);
      return;
    }

    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    setCrop(
      clampCrop(
        drag.originX + (event.clientX - drag.startX),
        drag.originY + (event.clientY - drag.startY),
      ),
    );
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    pointers.delete(event.pointerId);
    capturePointer(event.currentTarget, event.pointerId, false);

    if (pointers.size < 2 && pinchRef.current) {
      pinchRef.current = null;
      // One finger left on the glass: hand it the drag rather than making the
      // player lift off and start again.
      const [id] = [...pointers.keys()];
      const at = id === undefined ? undefined : pointers.get(id);
      dragRef.current =
        id === undefined || !at
          ? null
          : {
              pointerId: id,
              startX: at.x,
              startY: at.y,
              originX: cropRef.current.x,
              originY: cropRef.current.y,
            };
      return;
    }

    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  /** The touch gestures are drag and pinch; a keyboard or switch gets both. */
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? NUDGE * 3 : NUDGE;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setCrop(clampCrop(crop.x + move[0], crop.y + move[1]));
      return;
    }

    // `=` is the unshifted key `+` lives on, so both spellings work.
    const resize = event.key === '+' || event.key === '=' ? 1 : event.key === '-' ? -1 : 0;
    if (resize === 0) return;
    event.preventDefault();
    resizeTo(
      clampScale(scale) + resize * SCALE_STEP,
      crop.x + cropSize.width / 2,
      crop.y + cropSize.height / 2,
    );
  };

  /* ------------------------------------------------------------------ */
  /* Sources                                                             */
  /* ------------------------------------------------------------------ */

  /** Shared tail for both sources — they are the same path by design. */
  const accept = (next: Source) => {
    releaseSource();
    if (next.url.startsWith('blob:')) objectUrlRef.current = next.url;
    setSource(next);
    sound.play('blip');
    stop();
    setMode('preview');
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || busy) return;
    setBusy(true);
    try {
      const canvas = grabFrame(video);
      accept({
        drawable: canvas,
        // Display only, and only ever in this tab: the payload that goes on
        // the wire is encoded from the canvas at confirm time.
        url: canvas.toDataURL('image/jpeg', 0.9),
        from: 'camera',
        width: canvas.width,
        height: canvas.height,
      });
    } catch {
      setProblem('That did not come out. Try again, or choose a photo instead.');
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (file: File | undefined) => {
    if (!file || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const image = await loadImage(file);
      accept({
        drawable: image,
        url: image.src,
        from: 'upload',
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    } catch {
      setProblem("We couldn't read that image. Try a different one.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const retake = () => {
    const fromCamera = source?.from === 'camera';
    releaseSource();
    setSource(null);
    // The draft usually has no photo yet — nothing is committed until "Use
    // photo" — but the store owns the retake ledger, and this is the event it
    // counts. A second look at the camera is the thing SPEC §6 rations.
    clearDraftPhoto({ countsAsRetake: fromCamera });
    if (fromCamera && draft.retakesUsed < MAX_RETAKES) {
      void start();
    } else {
      setMode('choice');
    }
  };

  const cropRect = (): CropRect | null =>
    source
      ? windowToSourceRect(
          frame,
          { width: source.width, height: source.height },
          {
            x: crop.x,
            y: crop.y,
            width: cropSize.width,
            height: cropSize.height,
          },
        )
      : null;

  /**
   * The one moment the photo leaves the device. Resize and compression happen
   * here, on the region under the crop window, and only then is anything sent.
   */
  const confirm = async () => {
    const drawable = source?.drawable ?? photoRef.current;
    if (!source || !drawable || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const processed = await processPhoto(drawable, {
        crop: cropRect() ?? undefined,
      });
      setDraftPhoto(processed.dataUrl, source.from);
      submitProfile({ photo: processed.dataUrl });
      navigate(`/r/${roomCode}/name`, { replace: true });
    } catch (error) {
      setProblem(
        error instanceof PhotoTooLargeError
          ? "That image wouldn't compress small enough. Try a different one."
          : 'That photo would not save. Try another one.',
      );
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  const uploadInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/*"
      aria-label="Choose a photo"
      className="sr-only"
      onChange={(event) => void pickFile(event.target.files?.[0])}
    />
  );

  if (mode === 'preview' && source) {
    return (
      <Screen
        eyebrow="Photo preview"
        meta={
          source.from === 'camera' ? `${draft.retakesUsed + 1} of ${MAX_RETAKES + 1} retakes` : null
        }
        testId="photo-preview"
        // The deck's own spacing under the status row on crop + confirm. This
        // screen has no heading, so without it the frame starts hard against
        // the status row.
        bodyClassName="pt-3"
        footer={
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-3">
              <Button variant="secondary" onClick={retake} disabled={busy}>
                {source.from === 'camera' && !retakesSpent ? 'Retake' : 'Choose a photo'}
              </Button>
              <Button onClick={() => void confirm()} disabled={busy}>
                Use photo
              </Button>
            </div>
            <p id="crop-hint" className="text-center text-[0.6875rem] text-ink-soft">
              Drag to reposition · pinch to resize.
            </p>
          </div>
        }
      >
        {problem ? <Notice tone="warn">{problem}</Notice> : null}
        <div ref={frameRef} className={FRAME}>
          <img
            ref={photoRef}
            src={source.url}
            alt="Your photo"
            className="absolute inset-0 h-full w-full object-cover"
            onLoad={(event) => {
              // Now the true size is known, the crop maths can stop assuming.
              const image = event.currentTarget;
              if (!image.naturalWidth) return;
              setSource((current) =>
                current && current.width === image.naturalWidth
                  ? current
                  : current && {
                      ...current,
                      width: image.naturalWidth,
                      height: image.naturalHeight,
                    },
              );
            }}
          />
          <div
            role="group"
            tabIndex={0}
            aria-label="Card crop — drag or use the arrow keys to reposition, pinch or use + and − to resize"
            aria-describedby="crop-hint"
            data-testid="card-crop"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
            className="absolute touch-none border-2 border-white outline-none focus-visible:border-terracotta"
            style={{
              left: crop.x,
              top: crop.y,
              width: cropSize.width || undefined,
              height: cropSize.height || undefined,
              aspectRatio: cropSize.width ? undefined : `${PHOTO_MAX_WIDTH} / ${PHOTO_MAX_HEIGHT}`,
            }}
          >
            <span className="absolute top-1.5 left-2 text-[0.625rem] font-semibold tracking-[0.12em] text-white uppercase">
              Card crop
            </span>
          </div>
        </div>
        {uploadInput}
      </Screen>
    );
  }

  if (mode === 'live' || mode === 'starting') {
    return (
      <Screen
        eyebrow="Camera"
        title="Line up your face."
        testId="photo-live"
        footer={
          <div className="flex flex-col gap-2.5">
            <Button variant="equal" onClick={capture} disabled={busy || mode !== 'live'}>
              Take the photo
            </Button>
            <Button variant="equal-alt" onClick={() => fileRef.current?.click()} disabled={busy}>
              Choose a photo
            </Button>
            {uploadInput}
          </div>
        }
      >
        {problem ? <Notice tone="warn">{problem}</Notice> : null}
        <div className={FRAME}>
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            aria-label="Camera preview"
            className={`absolute inset-0 h-full w-full scale-x-[-1] object-cover ${
              mode === 'live' ? '' : 'opacity-0'
            }`}
          />
          {mode === 'starting' ? (
            <p className="absolute inset-0 flex items-center justify-center text-[0.8125rem] text-cream">
              Starting the camera…
            </p>
          ) : null}
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      eyebrow={`Room · ${roomCode}`}
      title="Add your face."
      sub="Every player becomes a card, so the group can rank people at a glance."
      testId="photo-choice"
      footer={
        <div className="flex flex-col gap-2.5">
          {cameraOffered ? (
            <Button variant="equal" onClick={() => void start()}>
              Take a selfie
            </Button>
          ) : null}
          {/* Equal, never a fallback (SPEC §6) — until there is no pair left to
              balance, at which point the survivor is simply the action. */}
          <Button
            variant={cameraOffered ? 'equal-alt' : 'primary'}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            Choose a photo
          </Button>
          {uploadInput}
        </div>
      }
    >
      {problem ? <Notice tone="warn">{problem}</Notice> : null}
      {/* One relaxed column, top-aligned. Centring this in the leftover space
          pushed the card into the middle of the screen and left two bands of
          bare sand around it; the deck stacks heading, copy, card, hint and
          the button pair at an even rhythm. */}
      <div className="flex flex-col items-center gap-4 pt-4">
        <LicenseCard
          variant="card"
          player={{
            id: 'example' as PlayerId,
            name: draft.name || 'You',
            photo: EXAMPLE_FACE,
          }}
          className="w-[16.25rem]"
        />
        <p className="text-center text-[0.6875rem] text-ink-soft">You can change the crop next.</p>
      </div>
    </Screen>
  );
}

/** jsdom, and a pointer that has already been released, both throw here. */
function capturePointer(element: Element, pointerId: number, hold: boolean): void {
  try {
    if (hold) element.setPointerCapture(pointerId);
    else element.releasePointerCapture(pointerId);
  } catch {
    /* the drag still tracks fine without it */
  }
}

/** The white box, sized off the frame it sits in and shaped like the card. */
function cropWindowSize(frame: Box): Box {
  if (frame.width <= 0 || frame.height <= 0) return { width: 0, height: 0 };
  let height = frame.height * CROP_HEIGHT_RATIO;
  let width = height * CARD_ASPECT;
  if (width > frame.width * CROP_MAX_WIDTH_RATIO) {
    width = frame.width * CROP_MAX_WIDTH_RATIO;
    height = width / CARD_ASPECT;
  }
  return { width: Math.round(width), height: Math.round(height) };
}
